import React, {useContext, useEffect, useRef, useMemo} from 'react';
import {AppContext} from '../../contexts/AppContext';
import {MapContainer, TileLayer, Marker, Popup, Circle, useMap} from 'react-leaflet';
import clsx from 'clsx';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import Skeleton from 'react-loading-skeleton';
import AccommodationTeaser from '../Common/AccommodationTeaser';
import {icon, latLngBounds} from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-gesture-handling';
import 'leaflet-gesture-handling/dist/leaflet-gesture-handling.css';
import 'react-leaflet-markercluster/dist/styles.min.css';

const FitBounds = ({pois}) => {
    const map = useMap();

    useEffect(() => {
        if (pois.length) {
            const bounds = latLngBounds(pois.map(poi => [poi.latitude, poi.longitude]));
            map.fitBounds(bounds, {
                padding: [50, 50],
                maxZoom: 16,
            });
        }
    }, [pois, map]);

    return null;
};

const Map = () => {

    const {isLoading, isMobile, selectedFilters, filteredResults, setCurrentMapPoi} = useContext(AppContext);
    const radius = selectedFilters.distance ? Number(selectedFilters.distance) : 20;
    const mapRef = useRef();

    let centerPoint = [47.836249, 13.0615383];
    let showRadius = false;
    if(selectedFilters.lat && selectedFilters.lng) {
        centerPoint = [selectedFilters.lat, selectedFilters.lng];
        showRadius = true;
    }

    // Memoize the pois array to prevent unnecessary re-renders
    const pois = useMemo(
        () => filteredResults.filter(poi => poi.latitude && poi.longitude),
        [filteredResults]
    );

    const handleMarkerClick = (poi) => {
        setCurrentMapPoi(poi);
    };

    const getIcons = (isPzg = false) => {
        const getIconPath = (name) => `/static/images/icons/map/${name}${isPzg ? '-pzg' : ''}.svg`;

        return {
            basic: icon({ iconUrl: getIconPath('basic'), iconSize: [46, 67], iconAnchor: [23, 67], popupAnchor: [0, -67] }),
            market: icon({ iconUrl: getIconPath('market'), iconSize: [46, 67], iconAnchor: [23, 67], popupAnchor: [0, -67] }),
            bar: icon({ iconUrl: getIconPath('bar'), iconSize: [46, 67], iconAnchor: [23, 67], popupAnchor: [0, -67] }),
            basicBioCrown: icon({ iconUrl: getIconPath('basicBioCrown'), iconSize: [46, 67], iconAnchor: [23, 67], popupAnchor: [0, -67] }),
            basicBio: icon({ iconUrl: getIconPath('basicBio'), iconSize: [46, 67], iconAnchor: [23, 67], popupAnchor: [0, -67] }),
            main: icon({ iconUrl: getIconPath('main'), iconSize: [69, 100], iconAnchor: [35, 100], popupAnchor: [0, -100] }),
        }
    };

    const icons = getIcons(window._config?.pzg);

    const getIcon = (mapMarker) => {
        return icons[mapMarker] || icons.basic;
    }

    return (
        <div className={clsx('accommodation-overview--map', isMobile && 'accommodation-overview--map--mobile')}>
            {isLoading ? (
                <Skeleton className="leaflet-map"/>
            ) : (
                <MapContainer
                    ref={mapRef}
                    center={centerPoint}
                    zoom={16}
                    minZoom={isMobile ? 7 : 8}
                    maxZoom={18}
                    zoomControl={true}
                    scrollWheelZoom={false}
                    gestureHandling={true}
                    className="leaflet-map"
                >
                    <TileLayer
                        url="https://{s}.wien.gv.at/basemap/geolandbasemap/normal/google3857/{z}/{y}/{x}.png"
                        subdomains={['mapsneu']}
                        attribution='Datenquelle: <a href="https://www.basemap.at">basemap.at</a>'
                    />
                    {/* alternative color: blue */}
                    {showRadius && <Circle
                        center={centerPoint}
                        radius={radius * 1000}
                        pathOptions={{ color: '#475949', fillOpacity: 0.1, weight: 3 }}
                    />}
                    <FitBounds pois={pois}/>
                    {showRadius ? (
                        // Render without clustering
                        pois.map(poi => (
                            <Marker
                                key={poi.id}
                                position={[poi.latitude, poi.longitude]}
                                icon={getIcon(poi.mapMarker)}
                                eventHandlers={{ click: () => handleMarkerClick(poi) }}
                            >
                                {!isMobile && (
                                    <Popup>
                                        <AccommodationTeaser data={poi} />
                                    </Popup>
                                )}
                            </Marker>
                        ))
                    ) : (
                        // Render with clustering
                        <MarkerClusterGroup
                            showCoverageOnHover={false}
                            spiderfyOnMaxZoom={false}
                            disableClusteringAtZoom={16}
                            maxClusterRadius={40}
                        >
                            {pois.map(poi => (
                                <Marker
                                    key={poi.id}
                                    position={[poi.latitude, poi.longitude]}
                                    icon={getIcon(poi.mapMarker)}
                                    eventHandlers={{ click: () => handleMarkerClick(poi) }}
                                >
                                    {!isMobile && (
                                        <Popup>
                                            <AccommodationTeaser data={poi} />
                                        </Popup>
                                    )}
                                </Marker>
                            ))}
                        </MarkerClusterGroup>
                    )}
                </MapContainer>
            )}
        </div>
    );
};

export default Map;
